import React, { useEffect, useState } from "react";
import { AutoComplete, Button, DatePicker, Form, Select, Spin } from "antd";
import dayjs from "dayjs";

import Devices from "services/devices";
import { Device } from "types";
import { DEFAULT_DATETIME_FORMAT } from "utils/datetime";
import { SCMaxDLogsFilter } from "../../types";

const { Option } = Select
const { RangePicker } = DatePicker

interface SCMaxDLogsFiltersProps {
	onSubmit: (values: any) => void;
	loading: boolean
	filters?: SCMaxDLogsFilter
}

const SCMaxDLogsFilters: React.FC<SCMaxDLogsFiltersProps> = ({ onSubmit, filters, loading }) => {
	// ----- Data -----
	const [device, setDevices] = useState<Device[]>([]);
	const [loadingDevices, setLoadingDevices] = useState(false);
	// ----- Form -----
	const [form] = Form.useForm();

	useEffect(() => {
		if (!filters) return
		const types = Array.isArray(filters?.rids) ? filters?.rids : (filters?.rids ? [filters?.rids] : [])
		form.setFieldsValue({
			types: types,
			dateRange: filters?.startDate && filters?.endDate && [dayjs(filters?.startDate), dayjs(filters?.endDate)],
			macAddress: filters?.macAddress
		})
	}, [filters])

	const fetchMacAddresses = async (value: any) => {
		if (!value || value.length < 3) {
			setDevices([]);
			return
		}
		setLoadingDevices(true);
		try {
			const params = {
				filters: {
					mac_address: value
				},
			}
			const response = await Devices.getAllUsersDevices(params);
			const responseData = response.data.items || [];
			setDevices(responseData);
		} catch (err) {
			// console.error('Error fetching')
		}
		setLoadingDevices(false);
	};

	const handleFinish = (values: any) => {
		const startDate = values?.dateRange && values?.dateRange[0] || null
		const endDate = values?.dateRange && values?.dateRange[1] || null
		const filters: SCMaxDLogsFilter = {
			rids: values?.rids || [],
			startDate: startDate && dayjs(startDate).format(DEFAULT_DATETIME_FORMAT) || null,
			endDate: endDate && dayjs(endDate).format(DEFAULT_DATETIME_FORMAT) || null,
			macAddress: values?.macAddress || null
		}
		onSubmit(filters);
	}

	return (
		<>
			<Form layout="vertical" form={form} onFinish={handleFinish}
				initialValues={{ macAddress: '' }}
			>
				<Form.Item
					name="macAddress"
					label="MAC Address"
					rules={[{ required: true, message: "Please input a valid mac address" }]}
				>
					<AutoComplete
						options={device.map(item => ({ value: item.macAddress, key: item.macAddress }))}
						onSearch={fetchMacAddresses}
					/>
				</Form.Item>
				<Spin size="small" spinning={loadingDevices} />
				<Form.Item name="rids" label="Types">
					<Select allowClear mode="multiple" optionFilterProp="key">
						<Option key="SelectFilterTimestamp" value="1">Timestamp</Option>
						<Option key="SelectFilterRebootShutdown" value="2">RebootShutdown</Option>
						<Option key="SelectFilterButton" value="3">Button</Option>
						<Option key="SelectFilterSerialNumber" value="4">SerialNumber</Option>
						<Option key="SelectFilterBLEConnect" value="5">BLEConnect</Option>
						<Option key="SelectFilterBLEDisconnect" value="6">BLEDisconnect</Option>
						<Option key="SelectFilterTimeJump" value="7">TimeJump</Option>
						<Option key="SelectFilterBattery" value="8">Battery</Option>
						<Option key="SelectFilterFlashWipe" value="9">FlashWipe</Option>
						<Option key="SelectFilterHardware" value="10">Hardware</Option>
						<Option key="SelectFilterSleepStatus" value="11">SleepStatus</Option>
						<Option key="SelectFilterBLESession" value="12">BLESession</Option>
						<Option key="SelectFilterPage" value="13">Page</Option>
						<Option key="SelectFilterActivity" value="14">Activity</Option>
						<Option key="SelectFilterNap" value="15">Nap</Option>
						<Option key="SelectFilterAlarm" value="16">Alarm</Option>
						<Option key="SelectFilterDoubleTap" value="21">DoubleTap</Option>
						<Option key="SelectFilterBacklight" value="24">Backlight</Option>
						<Option key="SelectFilterZap" value="32">Zap</Option>
						<Option key="SelectFilterRadio" value="42">Radio</Option>
						<Option key="SelectFilterFlashlight" value="43">Flashlight</Option>
						<Option key="SelectFilterEnergy" value="56">Energy</Option>
						<Option key="SelectFilterBeepStats" value="59">BeepStats</Option>
						<Option key="SelectFilterVibeStats" value="60">VibeStats</Option>
						<Option key="SelectFilterTrace" value="61">Trace</Option>
						<Option key="SelectFilterTemperature" value="64">Temperature</Option>
						<Option key="SelectFilterHeartRate" value="65">HeartRate</Option>
						<Option key="SelectFilterSettings" value="66">Settings</Option>
						<Option key="SelectFilterTestData" value="255">TestData</Option>
					</Select>
				</Form.Item>
				<Form.Item name="dateRange" label="Date Range">
					<RangePicker showTime />
				</Form.Item>
				<Form.Item>
					<Button type="primary" htmlType="submit" loading={loading}>Filter</Button>
				</Form.Item>
			</Form>
		</>
	)
}

export default SCMaxDLogsFilters
